import React, { useState, forwardRef } from 'react'
import styled from 'styled-components'
import { FormInput, colors, transitions } from '../theme/sharedComponents'

const PasswordWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${colors.borderLight};
  border-radius: 0.5rem;
  overflow: hidden;
  transition: ${transitions.fast};

  &:focus-within {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.hoverTint};
  }
`

const PasswordField = styled(FormInput)`
  border: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1;
  min-width: 0;

  &:focus {
    border: none;
    box-shadow: none;
  }
`

const ToggleButton = styled.button`
  flex-shrink: 0;
  background: ${colors.backgroundAlt};
  border: none;
  border-left: 1px solid ${colors.borderLight};
  color: ${colors.textLightModeMuted};
  padding: 0 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: ${transitions.fast};

  &:hover:not(:disabled) {
    color: ${colors.textLightMode};
    background: ${colors.hoverTint};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

type StyledPasswordInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'type'> & {
  value?: string | number | string[]
}

export const StyledPasswordInput = forwardRef<HTMLInputElement, StyledPasswordInputProps>(
  ({ placeholder = 'Enter your password', disabled = false, ...rest }, ref) => {
    const [showPassword, setShowPassword] = useState(false)

    return (
      <PasswordWrapper>
        <PasswordField
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder}
          disabled={disabled}
          {...rest}
        />
        <ToggleButton
          type="button"
          onClick={() => setShowPassword(v => !v)}
          disabled={disabled}
          tabIndex={-1}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
        </ToggleButton>
      </PasswordWrapper>
    )
  }
)

StyledPasswordInput.displayName = 'StyledPasswordInput'
